/**
 * BaseSystem - Abstract base class for game systems
 * 
 * All game systems (Trampoline, Portal, Wind, etc.) extend this class.
 * Provides a consistent interface for create/update/destroy lifecycle.
 * 
 * @abstract
 * @module systems/BaseSystem
 */

export default class BaseSystem {
    /**
     * @param {Phaser.Scene} scene - The game scene
     */
    constructor(scene) {
        if (new.target === BaseSystem) {
            throw new Error('BaseSystem is abstract and cannot be instantiated directly');
        }
        
        this.scene = scene;
        this.isActive = false;
        this.items = [];
    }

    /**
     * Initialize the system with level configuration
     * @param {Object} config - Level configuration
     * @abstract
     */
    create(config) {
        throw new Error('create() must be implemented by subclass');
    }

    /**
     * Update the system each frame
     * @param {number} delta - Delta time in ms
     * @param {Object} player - Player reference for interactions
     */
    update(delta, player) {
        // Override in subclass if needed
    }

    /**
     * Handle collision with player
     * @param {Object} body - The colliding body
     * @param {Object} player - Player reference
     * @returns {boolean} True if collision was handled
     */
    handleCollision(body, player) {
        return false;
    }

    /**
     * Check if a body belongs to this system
     * @param {Object} body - Physics body to check
     * @returns {boolean} True if body belongs to this system
     */
    ownsBody(body) {
        return false;
    }

    /**
     * Cleanup and destroy all items
     */
    destroy() {
        this.items.forEach(item => {
            if (item.destroy) {
                item.destroy();
            }
            if (item.visual && item.visual.destroy) {
                item.visual.destroy();
            }
            if (item.body && this.scene.matter.world) {
                this.scene.matter.world.remove(item.body);
            }
        });
        this.items = [];
        this.isActive = false;
    }

    /**
     * Get all tracked bodies for visual sync
     * @returns {Array} Array of tracked body objects
     */
    getTrackedBodies() {
        return [];
    }
}
